import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Section = ({ number, title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-white mb-4">
      <span className="text-[rgb(197,155,47)]">{number}.</span> {title}
    </h2>
    <div className="space-y-3 text-[rgb(218,218,218)] leading-relaxed text-[15px]">{children}</div>
  </div>
);

const Li = ({ children }) => (
  <li className="flex items-start space-x-2">
    <span className="text-[rgb(197,155,47)] mt-1 flex-shrink-0">•</span>
    <span>{children}</span>
  </li>
);

const TermsOfService = () => {
  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[rgb(17,17,19)] text-white">
      {/* Top bar */}
      <div className="bg-[rgb(26,28,30)] border-b border-[rgba(255,255,255,0.08)] px-6 py-4">
        <div className="max-w-[800px] mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 text-[rgb(197,155,47)] hover:text-[rgb(224,194,95)] transition-colors duration-200">
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back to Eshwald Ops</span>
          </Link>
          <div className="flex items-center space-x-2">
            <img src="/eshwald-icon.jpg" alt="Eshwald Ops" className="h-7 w-7 rounded-md object-cover" />
            <span className="font-bold text-sm"><span className="text-white">Eshwald</span><span className="text-[rgb(197,155,47)]"> Ops</span></span>
          </div>
        </div>
      </div>

      <div className="max-w-[800px] mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center space-x-2 text-[rgb(197,155,47)] text-xs font-semibold mb-4">
            <div className="w-6 h-[2px] bg-gradient-to-r from-transparent to-[rgb(197,155,47)]"></div>
            <span>LEGAL</span>
            <div className="w-6 h-[2px] bg-gradient-to-l from-transparent to-[rgb(197,155,47)]"></div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3">
            Terms of <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Service</span>
          </h1>
          <p className="text-[rgb(161,161,170)] text-sm">Eshwald Ops &nbsp;·&nbsp; Effective Date: March 11, 2026</p>
        </div>

        <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-8">

          <Section number="1" title="Introduction and Acceptance of Terms">
            <p>Welcome to Eshwald Ops ("we," "us," "our," or the "Business"). These Terms of Service ("Terms") govern your access to and use of the Eshwald Ops website and any related services (collectively, the "Services").</p>
            <p>By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.</p>
          </Section>

          <Section number="2" title="Description of Services">
            <p className="mb-3">Eshwald Ops is a creator-focused digital product partnership business that helps creators build sustainable income streams. Our services include:</p>
            <ul className="space-y-1 mb-4">
              <Li>Digital product development and strategy consultation</Li>
              <Li>Business operations support and management</Li>
              <Li>Customer support services for digital products</Li>
              <Li>Scaling and growth assistance for creator businesses</Li>
            </ul>
            <p>We operate on a zero upfront cost model, meaning we only succeed when our partner creators succeed. Specific partnership terms will be established through separate agreements negotiated off-site.</p>
          </Section>

          <Section number="3" title="User Responsibilities">
            <p className="mb-3">By using our Services, you agree to:</p>
            <ul className="space-y-2">
              <Li>Provide accurate, current, and complete information when submitting inquiries</Li>
              <Li>Use the Services only for lawful purposes and in accordance with these Terms</Li>
              <Li>Not engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Services</Li>
              <Li>Not attempt to gain unauthorized access to any portion of the Services or connected systems</Li>
              <Li>Not use the Services to transmit any unlawful, harassing, defamatory, abusive, or harmful material</Li>
              <Li>Not impersonate any person or entity or falsely state your affiliation with any person or entity</Li>
            </ul>
          </Section>

          <Section number="4" title="Intellectual Property">
            <p className="mb-3">All content, features, and functionality of our Services — including text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software — are the exclusive property of Eshwald Ops or its content suppliers and are protected by applicable intellectual property laws.</p>
            <p>You may not reproduce, distribute, modify, create derivative works of, publicly display, or transmit any materials from our Services without our prior written consent, except as permitted by applicable law.</p>
          </Section>

          <Section number="5" title="Disclaimer of Warranties">
            <div className="bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 mb-4">
              <p className="text-sm uppercase tracking-wide text-[rgb(197,155,47)] font-semibold mb-2">Important Notice</p>
              <p className="text-sm">THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, ESHWALD OPS DISCLAIMS ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
            </div>
            <p>We do not warrant that the Services will be uninterrupted, timely, secure, or error-free. No advice or information, whether oral or written, obtained from Eshwald Ops shall create any warranty not expressly stated in these Terms.</p>
          </Section>

          <Section number="6" title="Limitation of Liability">
            <div className="bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 mb-4">
              <p className="text-sm uppercase tracking-wide text-[rgb(197,155,47)] font-semibold mb-2">Important Notice</p>
              <p className="text-sm">TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ESHWALD OPS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES RESULTING FROM YOUR USE OF OR INABILITY TO USE THE SERVICES.</p>
            </div>
            <p>In jurisdictions where limitations of liability are not permitted, our liability shall be limited to the maximum extent permitted by law.</p>
          </Section>

          <Section number="7" title="Indemnification">
            <p>You agree to defend, indemnify, and hold harmless Eshwald Ops, its owner, and any affiliates from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Services.</p>
          </Section>

          <Section number="8" title="Governing Law and Dispute Resolution">
            <p className="mb-3">These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.</p>
            <p>Any disputes arising out of or relating to these Terms shall first be attempted to be resolved through good-faith negotiation. If unresolved, both parties agree to attempt mediation before pursuing any other form of dispute resolution.</p>
          </Section>

          <Section number="9" title="Modifications to Terms">
            <p>We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will make reasonable efforts to provide notice prior to any new terms taking effect. Your continued use of the Services after any changes constitutes your acceptance of the new Terms.</p>
          </Section>

          <Section number="10" title="Severability">
            <p>If any provision of these Terms is held to be invalid, illegal, or unenforceable, such provision shall be modified to the minimum extent necessary to make it valid and enforceable, or if not possible, shall be severed from these Terms. The remaining provisions shall continue in full force and effect.</p>
          </Section>

          <Section number="11" title="Entire Agreement">
            <p>These Terms, together with any separate partnership agreements executed between you and Eshwald Ops, constitute the entire agreement between you and Eshwald Ops regarding the use of our Services and supersede all prior and contemporaneous understandings, agreements, and warranties.</p>
          </Section>

          <Section number="12" title="Contact Information">
            <p className="mb-2">If you have any questions about these Terms of Service, please contact us:</p>
            <div className="bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 inline-block">
              <p className="font-semibold text-white">Eshwald Ops</p>
              <a href="mailto:eshwaldsystems@gmail.com" className="text-[rgb(197,155,47)] hover:underline">eshwaldsystems@gmail.com</a>
            </div>
          </Section>

          <div className="mt-8 pt-8 border-t border-[rgba(255,255,255,0.08)] text-[rgb(161,161,170)] text-sm">
            <p>© 2026 Eshwald Ops. All rights reserved. Last updated: March 11, 2026.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
